import { DoubleIndex, Logger, SingleIndex } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { Vote } from './Vote';

const metrics = Logger.get('app.metrics');

export type VoteIndexKey = readonly [string, string];

export class VoteIndex extends ActiveObjectWithEvents<{ updated: VoteIndexKey[] }> {

    @resolve(ObjectManager) private objects!: ObjectManager;

    private readonly forwardIndex = new DoubleIndex<string, Vote>();
    private readonly reverseIndex = new SingleIndex<Vote, VoteIndexKey>();
    private readonly invalidations = new Set<Vote>();

    public start(): void {
        this.rebuild();
        this.burden(
            this.objects.on('Vote', 'allocated', this.doInvalidate),
            this.objects.on('Vote', 'affected', this.doInvalidate),
            this.objects.on('Vote', 'released', this.doInvalidate),
            this.objects.on('updated', this.doRefresh),
        );
    }

    public count(roundId: string, subjectId: string | '*') {
        return this.lookup(roundId, subjectId).size;
    }

    public lookup(roundId: string, subjectId: string | '*') {
        this.refresh();
        return this.forwardIndex.get(roundId, subjectId);
    }

    public dir() {
        return this.forwardIndex.dir();
    }

    private rebuild() {
        const t = performance.now();

        const results = this.objects.getAll(Vote);
        results.forEach(x => this.add(x));

        metrics.log('VoteIndex.rebuild', `${performance.now() - t}ms`);
    }

    private refresh() {
        const affectedKeys = [] as VoteIndexKey[];
        for (const v of this.invalidations) {
            affectedKeys.push(...this.remove(v));
            if (v.alive) {
                affectedKeys.push(...this.add(v));
            }
        }
        this.invalidations.clear();
        if (affectedKeys.length) {
            this.events.emit('updated', Array.from(affectedKeys));
        }
    }

    private add(v: Vote) {
        if (v.parent) {
            const keys = [
                ['*', v.parent.id] as const,
                [v.round, v.parent.id] as const,
                [v.round, v.userId ?? 'NULL_USER'] as const,
                [v.round, '*'] as const,
            ];
            for (const k of keys) {
                this.forwardIndex.add(k[0], k[1], v);
                this.reverseIndex.add(v, k);
            }
            return keys;
        }
        return [];
    }

    private remove(v: Vote) {
        const keys = this.reverseIndex.get(v);
        for (const [roundId, indexer] of keys) {
            this.forwardIndex.remove(roundId, indexer, v);
        }
        this.reverseIndex.delete(v);
        return Array.from(keys);
    }

    private doInvalidate = ([component]: [Component]) => {
        this.invalidations.add(component.cast(Vote));
    };

    private doRefresh = () => {
        this.refresh();
    };
}
